import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
const fast = {
  duration: 0.3,
  ease: 'easeOut' as const
};
interface HeroProps {
  onStartQuiz: () => void;
}
export function Hero({
  onStartQuiz
}: HeroProps) {
  return <section className="relative pt-[56px] md:pt-[72px] bg-primary overflow-hidden" data-id="element-76">
      <div className="absolute inset-0 hero-light-streaks z-0" data-id="element-77"></div>

      <div className="max-w-[1400px] w-full mx-auto px-5 md:px-12 lg:px-[72px] relative z-10 py-6 lg:py-24 flex flex-col lg:flex-row items-center" data-id="element-78">
        {/* Left Content */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={fast} className="w-full lg:w-[50%] flex flex-col items-start z-20" data-id="element-79">
          <div className="flex items-center space-x-3 mb-4 lg:mb-8" data-id="element-80">
            <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-accent" data-id="element-81">
              NUTRICIÓN PERSONALIZADA · MÉXICO
            </span>
          </div>

          <h1 className="text-[36px] md:text-[68px] leading-[1.05] mb-4 lg:mb-8" data-id="element-82">
            <span className="block font-serif font-normal text-text-primary" data-id="element-83">
              Tu plan de alimentación.
            </span>
            <span className="block font-serif font-normal text-text-primary" data-id="element-84">
              Con un nutriólogo real.
            </span>
            <span className="block font-serif italic font-normal text-accent" data-id="element-85">
              Desde tu casa.
            </span>
          </h1>

          <p className="text-text-secondary text-[14px] md:text-base leading-[1.6] lg:leading-[1.7] max-w-[420px] mb-6 lg:mb-8 font-light" data-id="element-86">
            Conectamos personas como tú con nutriólogos certificados que diseñan
            planes de alimentación adaptados a tu rutina, tus gustos y tus
            objetivos. Todo por videollamada.
          </p>

          <div className="flex flex-col sm:flex-row w-full sm:w-auto items-start sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 mb-6 lg:mb-8" data-id="element-87">
            <motion.button onClick={onStartQuiz} whileTap={{
            scale: 0.96
          }} className="w-full sm:w-auto bg-dark text-white uppercase text-[14px] font-bold tracking-wide py-4 px-8 rounded-full flex items-center justify-center space-x-2 min-h-[56px] shadow-lg hover:bg-dark/90 hover:shadow-xl transition-all" data-id="element-88">
  
  <span data-id="element-89">HACER TEST GRATUITO →</span>
</motion.button>
            <motion.button whileTap={{
            scale: 0.96
          }} className="w-full sm:w-auto border border-dark text-dark uppercase text-[14px] font-bold tracking-wide py-4 px-8 rounded-full flex items-center justify-center space-x-2 min-h-[56px] hover:bg-dark hover:text-white transition-all" data-id="element-90">
              <span data-id="element-91">Ver cómo funciona ↓</span>
            </motion.button>
          </div>

          {/* Social proof */}
          <div className="flex items-center space-x-3 mb-6" data-id="element-92">
            <div className="flex -space-x-2" data-id="element-93">
              {['https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop', 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop', 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop', 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop'].map((src, i) => <img key={i} src={src} alt="User" className="w-7 h-7 rounded-full border-2 border-primary object-cover" data-id="element-94" />)}
            </div>
            <p className="text-[12px] text-text-primary" data-id="element-95">
              <span className="font-bold" data-id="element-96">+12,000 personas</span>{' '}
              <span className="font-normal" data-id="element-97">ya transformaron su vida</span>
            </p>
          </div>

          {/* Trust bullets */}
          <div className="flex flex-col space-y-2" data-id="element-98">
            {['Nutriólogos con cédula profesional SEP', 'Primera evaluación 100% gratuita', 'Planes desde $499 MXN'].map(t => <span key={t} className="flex items-center space-x-2 text-[13px] text-text-secondary" data-id="element-99">
                <Check className="w-4 h-4 text-accent" data-id="element-100" />
                <span data-id="element-101">{t}</span>
              </span>)}
          </div>
        </motion.div>

        {/* Image & Visual Composition — responsive */}
        <div className="w-full lg:w-[50%] relative mt-10 lg:mt-0 flex flex-col items-center lg:items-end lg:h-[700px] lg:justify-center" data-id="element-102">
          {/* Rotating Badge — hidden on mobile, shown on lg */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="hidden lg:block absolute top-12 right-32 z-30 w-[120px] h-[120px]" data-id="element-103">
            <svg viewBox="0 0 100 100" className="w-full h-full animate-spin-slow" data-id="element-104">
              <path id="circlePath" d="M 50, 50 m -37, 0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" fill="none" data-id="element-105" />
              <text className="text-[10px] font-bold tracking-[0.15em] uppercase fill-text-primary" data-id="element-106">
                <textPath href="#circlePath" startOffset="0%" data-id="element-107">
                  NUTRICIÓN REAL · RESULTADOS · REAL ·
                </textPath>
              </text>
            </svg>
          </motion.div>

          {/* Main Food Image */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1,
          ease: 'easeOut'
        }} className="relative z-10 w-[85%] max-w-[320px] lg:w-[90%] lg:max-w-[500px] aspect-square rounded-3xl lg:rounded-full overflow-hidden shadow-xl lg:shadow-2xl shadow-dark/10" data-id="element-108">
            <img src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1000&q=80" alt="Healthy bowl with quinoa, avocado, and chickpeas" className="w-full h-full object-cover scale-105" data-id="element-109" />
          </motion.div>

          {/* Quote Card */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.3,
          ease: 'easeOut'
        }} className="relative z-20 -mt-8 mx-auto lg:mx-0 w-[90%] max-w-[320px] lg:w-[280px] lg:max-w-none lg:absolute lg:bottom-24 lg:-right-8 bg-dark rounded-2xl lg:rounded-none p-5 lg:p-8 shadow-xl" data-id="element-110">
            <p className="font-serif italic text-[16px] lg:text-[22px] text-primary leading-snug mb-0" data-id="element-111">
              "Comer bien no es comer menos, es comer mejor."
            </p>
          </motion.div>
        </div>
      </div>
    </section>;
}